import { PROGRESS_DEFAULT_WEEKS } from "@repo/contract/training";
/**
 * `GET /me/pending`'s key (T38) — ONE definition, in `data/plan/keys.ts`,
 * because T32's draft writes needed it first. Re-exported so the pending
 * query and every write that invalidates it name the same array.
 */
export { pendingKeys } from "../plan/keys";
/**
 * Query keys for the progress domain (`../core/keys.ts` is the convention).
 *
 * `["progress"]` is the domain's prefix: the aggregates here (T33) and what
 * T34 (the Retrospectiva) and T36 (insights) add sit under it, so a write
 * that changes the training log — ending a session, correcting or removing
 * a set — invalidates every one of them with `progressKeys.all`.
 *
 * The weeks AND the locale are in the key: eight weeks and twenty-six are
 * different answers, and muscle and exercise names are localized. An
 * omitted value is written as the server's own default (8 weeks, `"en"`), so
 * it never makes a second key for the same answer.
 */
export const DEFAULT_PROGRESS_LOCALE = "en";
export const progressKeys = {
    all: ["progress"],
    /** `GET /me/progress` for one range and locale. */
    aggregates: (weeks = PROGRESS_DEFAULT_WEEKS, locale = DEFAULT_PROGRESS_LOCALE) => ["progress", "aggregates", weeks, locale],
    aggregatesAll: ["progress", "aggregates"],
    /**
     * `GET /me/retrospective` for one week and locale (T34). An omitted week is
     * keyed `"latest"`, NOT a computed `YYYY-Www`: which week is "the last
     * complete one" is the server's clock's answer, and the key must not
     * guess it on the device's.
     */
    retrospective: (week, locale = DEFAULT_PROGRESS_LOCALE) => ["progress", "retrospective", week ?? "latest", locale],
    /**
     * A month's or a year's story (`D204`): `"month:latest"`, `"year:2025"` in
     * the week's slot, so it sits under {@link retrospectiveAll} and a session
     * end invalidates it with the rest — and can never collide with a week,
     * whose names have no colon.
     */
    retrospectivePeriod: (period, name, locale = DEFAULT_PROGRESS_LOCALE) => [
        "progress",
        "retrospective",
        `${period}:${name ?? "latest"}`,
        locale,
    ],
    /** `GET /me/recovery` for one locale (`D203`). Under `["progress"]`, so a
     *  session end or a set correction re-reads it. */
    recovery: (locale = DEFAULT_PROGRESS_LOCALE) => ["progress", "recovery", locale],
    /** `GET /me/lifetime` (`D204`). Nothing in it is localised. */
    lifetime: ["progress", "lifetime"],
    retrospectiveAll: ["progress", "retrospective"],
    /**
     * `GET /me/report` for one span and locale (`D252`). An omitted name is
     * `"latest"`, as the Retrospectiva's: which span last ended is the
     * server's clock's answer. Under `["progress"]`, so a session end or a set
     * correction re-reads it.
     */
    report: (period, name, locale = DEFAULT_PROGRESS_LOCALE) => ["progress", "report", period, name ?? "latest", locale],
    /**
     * `GET /me/insight` for one locale (T36). Under `["progress"]`, so ending
     * a session or correcting a set — the writes every rule reads — already
     * invalidates it.
     */
    insight: (locale = DEFAULT_PROGRESS_LOCALE, language) => language === undefined
        ? ["progress", "insight", locale]
        : ["progress", "insight", locale, language],
};
//# sourceMappingURL=keys.js.map